import {
  Check,
  ShieldCheck,
  ArrowRight,
  Inbox,
  GitBranch,
  Settings2,
  UserCheck,
  Infinity as InfinityIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Hero } from "@/components/Hero";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";
import { Button } from "@/components/Button";
import { IMG, VIDEO, POSTER } from "@/lib/images";
import { TEAMS } from "@/data/teams";
import { getJSON } from "@/lib/api";
import { VideoTile } from "@/components/VideoTile";

const Label = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ember)]">
    {children}
  </span>
);

// ---- Editable content arrays ------------------------------------------------

const CAPABILITY_STRIP = [
  "Operations",
  "Finance & admin",
  "Reporting",
  "Tool integration",
  "Approval gates",
  "Ongoing tuning",
];

const PAYOFFS = [
  {
    n: "01",
    icon: Inbox,
    head: "Capture & intake",
    body: "Forms, calls, texts, and DMs answered and routed fast, so nothing sits.",
  },
  {
    n: "02",
    icon: GitBranch,
    head: "Follow-up & handoffs",
    body: "Research, summaries, reminders, and clean handoffs that keep work moving without babysitting.",
  },
  {
    n: "03",
    icon: Settings2,
    head: "Operations",
    body: "Scheduling, intake packets, reporting, and repeat admin, handled inside your existing stack.",
  },
  {
    n: "04",
    icon: UserCheck,
    head: "Owner control",
    body: "Exceptions escalated and approvals queued — leadership gets a clean queue, not another messy inbox.",
  },
];

const AUDIT_STEPS = [
  { n: "01", body: "Map the manual work your team repeats every week." },
  {
    n: "02",
    body: "Find the first workflow where speed, consistency, or cost is hurting most.",
  },
  {
    n: "03",
    body: "Design the solution: agent roles, tool connections, human approvals, launch sequence.",
  },
  {
    n: "04",
    body: "Install the first system, test on real examples, and tune before expanding.",
  },
];

const CATALOG = [
  {
    group: "Operations",
    agents: [
      "Client onboarder",
      "Scheduler",
      "Dispatcher",
      "SOP enforcer",
      "Weekly reporter",
      "Anomaly watcher",
      "Inventory planner",
      "Vendor coordinator",
      "Renewal reminder",
    ],
  },
  {
    group: "Finance & Admin",
    agents: [
      "Invoice sender",
      "AR chaser",
      "Expense categorizer",
      "Receipt filer",
      "Payroll prep",
      "Bookkeeping matcher",
      "Cashflow forecaster",
      "Document gatherer",
    ],
  },
  {
    group: "Reporting & Insight",
    agents: [
      "Weekly digest writer",
      "KPI reporter",
      "Anomaly flagger",
      "Deep-research writer",
      "Decision-memo drafter",
      "Competitor tracker",
    ],
  },
  {
    group: "Executive Ops",
    agents: [
      "Inbox triage",
      "Calendar concierge",
      "Meeting-prep briefer",
      "Priority watcher",
      "Follow-up tracker",
      "Decision-note organizer",
    ],
  },
];

const INTEGRATIONS = [
  "GoHighLevel",
  "HubSpot",
  "Salesforce",
  "Gmail",
  "Outlook",
  "Google Calendar",
  "Slack",
  "Notion",
  "Stripe",
  "QuickBooks",
  "Zapier",
  "Airtable",
  "Twilio",
  "Zendesk",
  "Intercom",
  "Pipedrive",
];

const PHASES = [
  {
    days: "Days 1–3",
    tag: "Free audit first",
    head: "Audit the workflow",
    body: "We map the repeat work, tools, permissions, edge cases, and approval rules.",
  },
  {
    days: "Days 4–10",
    tag: "You approve the map",
    head: "Design the solution",
    body: "We spec agent roles, prompts, handoffs, integrations, and success criteria before anything goes live.",
  },
  {
    days: "Days 11–21",
    tag: "Shadow mode",
    head: "Build and connect",
    body: "Wired into your existing stack and tested against real examples from the business.",
  },
  {
    days: "Days 22–30",
    tag: "Phased rollout",
    head: "Launch and tune",
    body: "Your team reviews outputs, we tighten failure modes, and routine work starts moving through the system.",
  },
];

const FAQ = [
  {
    q: "Is this another AI software subscription?",
    a: "No. It's done-for-you. We audit, design, connect, set approval rules, and tune after launch. You don't manage software.",
  },
  {
    q: "What should we automate first?",
    a: "Usually the highest-volume repeat workflow with clear inputs and expensive delays — lead response, follow-up, scheduling, intake, reporting. The audit finds the best first wedge.",
  },
  {
    q: "Do we have to replace our CRM or tools?",
    a: "Usually no. The fastest wins come from installing agents around the tools you already use, then improving handoffs inside that stack.",
  },
  {
    q: "Can customers tell they're talking to AI?",
    a: "Only if you want them to. Many installs start with internal drafts and human approval, then expand to customer-facing automation once it's proven safe.",
  },
  {
    q: "What if the AI makes a mistake?",
    a: "Sensitive actions pause for human approval. We set scopes, logs, fallbacks, and escalation so routine work runs without pretending every task is low-risk.",
  },
  {
    q: "How fast can the first system go live?",
    a: "The main path is 30 days: audit, design, build, shadow mode, phased launch. Complex access or compliance can change scope.",
  },
  {
    q: "What does it cost?",
    a: "It depends on workflow complexity. The audit comes first, so the recommendation is tied to a real operational win, not a generic package.",
  },
];

const INDUSTRIES = [
  ["Real Estate", "/real-estate"],
  ["Healthcare", "/healthcare"],
  ["Legal", "/legal"],
  ["Automotive", "/automotive"],
  ["Home Services", "/home-services"],
];

// -----------------------------------------------------------------------------

export default function Workflow() {
  const leader = TEAMS.engineering.leader;
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    getJSON("/projects")
      .then((rows) => setProjects((rows || []).filter((p) => (p.teams && p.teams.length ? p.teams : p.team ? [p.team] : []).includes("Workflow & Automation")).slice(0, 4)))
      .catch(() => setProjects([]));
  }, []);

  return (
    <>
      {/* 1. Hero */}
      <Hero
        image={POSTER.workflow}
        video={VIDEO.workflow}
        eyebrow="WORKFLOW & AUTOMATION"
        title="AI agents for the work that repeats."
        subline="We audit the repeat work inside your business, install AI agents around the tools you already use — with approvals, logs, and human control from day one — and fix the bottlenecks quietly draining your team."
        primary={{ label: "Install your department", to: "/audit" }}
      />

      {/* Capability strip */}
      <section
        className="border-b border-black/5 bg-[var(--bg-contrast)]"
        data-testid="workflow-capability-strip"
      >
        <div className="mx-auto max-w-7xl px-6 py-6 md:px-12">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px] font-medium text-[var(--muted)]">
            {CAPABILITY_STRIP.map((c, i) => (
              <span key={c} className="flex items-center gap-3">
                {i > 0 && <span className="text-[var(--ember)]/50">·</span>}
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 2. Where automation pays off first */}
      <section
        className="bg-[var(--bg-base)] py-28 md:py-36"
        data-testid="workflow-payoffs"
      >
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-2xl">
            <Reveal>
              <Label>Where automation pays off first</Label>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="mt-7 t-section">
                Start where repeat work is slowing the business.
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-4 text-base leading-relaxed text-[var(--muted)] sm:text-lg">
                We don't hand you a chatbot and wish you luck. We install
                automation around the places work gets stuck.
              </p>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2">
            {PAYOFFS.map((p, i) => (
              <Reveal key={p.head} delay={(i % 2) * 100}>
                <div className="group h-full rounded-2xl border border-black/5 bg-[var(--bg-contrast)] p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--ember)]/30 hover:shadow-[0_24px_50px_-28px_rgba(20,18,16,0.4)]">
                  <div className="flex items-center justify-between">
                    <span className="text-4xl font-semibold tracking-[-0.03em] text-[var(--ember)]">
                      {p.n}
                    </span>
                    <p.icon size={26} className="text-[var(--ember)]" strokeWidth={1.6} />
                  </div>
                  <h3 className="mt-6 t-sub">
                    {p.head}
                  </h3>
                  <p className="mt-4 text-[15px] leading-relaxed text-[var(--muted)]">
                    {p.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 3. The free workflow audit */}
      <section
        className="bg-[var(--bg-contrast)] py-28 md:py-36"
        data-testid="workflow-audit"
      >
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-2xl">
            <Reveal>
              <Label>The free workflow audit</Label>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="mt-7 t-section">
                Know what to install before you buy anything.
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-4 text-base leading-relaxed text-[var(--muted)] sm:text-lg">
                The first call isn't a generic AI pitch. We pressure-test where
                automation removes repeat work, what needs human approval, and
                whether the first install is even worth it.
              </p>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {AUDIT_STEPS.map((s, i) => (
              <Reveal key={s.n} delay={(i % 4) * 80}>
                <div className="flex h-full flex-col rounded-2xl border border-black/5 bg-[var(--bg-base)] p-7">
                  <span className="text-3xl font-semibold tracking-[-0.03em] text-[var(--ember)]">
                    {s.n}
                  </span>
                  <p className="mt-5 text-[15px] leading-relaxed text-[var(--ink)]">
                    {s.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={120}>
            <div className="mt-12">
              <Button to="/audit" variant="primary" data-testid="workflow-audit-cta">
                Install your department
              </Button>
              <p className="mt-4 text-sm text-[var(--green-ink)]">
                Free Business DNA audit. Six scores. Not a pitch.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4. The operations agent catalog */}
      <section
        className="bg-[var(--bg-base)] py-28 md:py-36"
        data-testid="workflow-catalog"
      >
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-2xl">
            <Reveal>
              <Label>The operations agent catalog</Label>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="mt-7 t-section">
                If the business repeats it, an agent can run it.
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-4 text-base leading-relaxed text-[var(--muted)] sm:text-lg">
                No fixed feature list. We build around your actual bottleneck,
                then expand only after the first workflow is safe and useful.
              </p>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CATALOG.map((col, i) => (
              <Reveal key={col.group} delay={(i % 4) * 80}>
                <div className="h-full rounded-2xl border border-black/5 bg-[var(--bg-contrast)] p-7">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ember)]">
                    {col.group}
                  </p>
                  <ul className="mt-5 space-y-3">
                    {col.agents.map((a) => (
                      <li
                        key={a}
                        className="flex items-start gap-2.5 text-[14px] leading-snug text-[var(--ink)]"
                      >
                        <span className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--ember)]/60" />
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Infinity card */}
          <Reveal delay={120}>
            <div className="mt-6 flex flex-col items-start gap-6 rounded-2xl border-2 border-[var(--ember)]/40 bg-[var(--ember)]/[0.05] p-8 md:flex-row md:items-center md:justify-between md:p-10">
              <div className="flex items-start gap-5">
                <InfinityIcon size={34} className="mt-1 flex-shrink-0 text-[var(--ember)]" strokeWidth={1.6} />
                <div>
                  <h3 className="t-sub">
                    Don't see it here?
                  </h3>
                  <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-[var(--muted)]">
                    That's the point. We design automation around your workflow,
                    not a fixed menu. Free audit, practical workflow map first.
                  </p>
                </div>
              </div>
              <Button href="#closing" variant="primary" data-testid="workflow-catalog-cta">
                Tell us what to automate
              </Button>
            </div>
          </Reveal>

          {/* Front-office note */}
          <Reveal delay={160}>
            <p className="mt-8 max-w-3xl text-[14px] leading-relaxed text-[var(--muted)]">
              Looking for customer-facing agents — lead response, nurture,
              content, reviews? Those live on your industry page, powered by{" "}
              <Link to="/growth" className="font-semibold text-[var(--ember)] hover:underline">
                the Growth Team
              </Link>
              . See the{" "}
              <Link to="/real-estate" className="font-semibold text-[var(--ember)] hover:underline">
                industry pages
              </Link>
              .
            </p>
          </Reveal>
        </div>
      </section>

      {/* 5. Integrations strip */}
      <section
        className="bg-[var(--dark)] py-24 text-[var(--dark-text)] md:py-28"
        data-testid="workflow-integrations"
      >
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-2xl">
            <Reveal>
              <Label>Installed around the tools you already use</Label>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="mt-7 t-section">
                No rip-and-replace — we improve the handoffs inside your current
                stack.
              </h2>
            </Reveal>
          </div>
        </div>
        <div
          className="marquee mt-14 flex gap-4 overflow-hidden"
          aria-label="Integration partners"
        >
          <div className="marquee-track flex flex-shrink-0 gap-4 pr-4">
            {INTEGRATIONS.concat(INTEGRATIONS).map((name, i) => (
              <span
                key={`${name}-${i}`}
                className="flex-shrink-0 whitespace-nowrap rounded-full border border-white/12 bg-white/[0.04] px-6 py-3 text-[15px] font-medium text-white/80"
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 6. The 30-day first install path */}
      <section
        id="path"
        className="scroll-mt-24 bg-[var(--bg-base)] py-28 md:py-36"
        data-testid="workflow-path"
      >
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-2xl">
            <Reveal>
              <Label>The 30-day first install path</Label>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="mt-7 t-section">
                From messy workflow to live agent system.
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-4 text-base leading-relaxed text-[var(--muted)] sm:text-lg">
                A service, not a software product. We scope the first wedge,
                build it, connect it, test it, and tune it with your team.
              </p>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {PHASES.map((p, i) => (
              <Reveal key={p.head} delay={(i % 4) * 80}>
                <div className="flex h-full flex-col rounded-2xl border border-black/5 bg-[var(--bg-contrast)] p-7">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ember)]">
                    {p.days}
                  </span>
                  <h3 className="mt-4 text-xl font-semibold tracking-[-0.02em]">
                    {p.head}
                  </h3>
                  <p className="mt-4 flex-1 text-[15px] leading-relaxed text-[var(--muted)]">
                    {p.body}
                  </p>
                  <span className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-[var(--ember)]/10 px-4 py-2 text-[13px] font-semibold text-[var(--ember)]">
                    {p.tag}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={120}>
            <div className="mt-12 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <Button href="#closing" variant="primary" data-testid="workflow-path-cta">
                Map my first install
              </Button>
              <span className="text-[14px] text-[var(--muted)]">
                We'll tell you honestly whether the workflow is ready.
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 7. Selected work */}
      {projects.length > 0 && (
        <section className="bg-[var(--charcoal)] py-28 text-white md:py-36" data-testid="workflow-work">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div className="max-w-2xl">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--mint)]">Selected work</span>
                <Reveal delay={80}>
                  <h2 className="mt-7 t-section text-white">Systems we've shipped.</h2>
                </Reveal>
                <Reveal delay={140}>
                  <p className="mt-4 text-base leading-relaxed text-white/60 sm:text-lg">
                    A sample of the automations and agents we've built and put live for teams like yours.
                  </p>
                </Reveal>
              </div>
              <Reveal delay={160}>
                <Link to="/work" className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--mint)] hover:underline" data-testid="workflow-work-link">
                  See the full portfolio <ArrowRight size={15} />
                </Link>
              </Reveal>
            </div>
            <div className="mt-12 grid gap-6 sm:grid-cols-2">
              {projects.map((p, i) => (
                <VideoTile key={p.slug} project={p} delay={(i % 2) * 90} aspect="aspect-[4/5]" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 8. Meet the leader + AI Standards */}
      <section
        className="bg-[var(--bg-base)] py-28 md:py-36"
        data-testid="workflow-leader"
      >
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 md:px-12 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-2xl border border-black/5">
              <img
                src={leader.photo}
                alt={leader.name}
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <Label>Meet the team leader</Label>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="mt-7 t-section">
                {leader.name}
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-2 text-sm font-semibold uppercase tracking-[0.1em] text-[var(--muted)]">
                {leader.title} · directs the Workflow Agent
              </p>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-4 text-base leading-relaxed text-[var(--muted)]">
                {leader.bio}
              </p>
            </Reveal>
            <Reveal delay={260}>
              <div className="mt-8 flex items-start gap-3 rounded-2xl border border-[var(--ember)]/30 bg-[var(--ember)]/[0.05] p-6">
                <ShieldCheck size={20} className="mt-0.5 flex-shrink-0 text-[var(--ember)]" />
                <p className="text-[15px] leading-relaxed text-[var(--ink)]">
                  Every automation is reviewed by a human before it goes live —
                  sensitive actions always pause for approval. That's AI
                  Standards.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section
        className="bg-[var(--bg-contrast)] py-28 md:py-36"
        data-testid="workflow-faq"
      >
        <div className="mx-auto max-w-3xl px-6 md:px-12">
          <Reveal>
            <Label>FAQ</Label>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="mt-7 t-section">
              Clear answers before you install.
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <Accordion type="single" collapsible className="mt-10">
              {FAQ.map((item, i) => (
                <AccordionItem
                  key={item.q}
                  value={`faq-${i}`}
                  className="border-black/10"
                  data-testid={`workflow-faq-item-${i}`}
                >
                  <AccordionTrigger className="py-5 text-left text-lg font-semibold tracking-[-0.01em] text-[var(--ink)] hover:no-underline">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-[var(--muted)]">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Powers agents across your industries — cross-link */}
      <section
        className="bg-[var(--bg-base)] py-24 md:py-28"
        data-testid="workflow-industries-crosslink"
      >
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <Label>Powers agents across your industries</Label>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="mt-5 max-w-2xl text-2xl font-semibold leading-tight tracking-[-0.02em] sm:text-3xl">
              This team runs inside every roster we install.
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {INDUSTRIES.map(([name, route], i) => (
              <Reveal key={route} delay={i * 70}>
                <Link
                  to={route}
                  className="group flex items-center justify-between rounded-2xl border border-black/10 bg-[var(--bg-contrast)] px-5 py-5 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--ember)]/40"
                  data-testid={`workflow-crosslink-${route.slice(1)}`}
                >
                  <span className="text-[14px] font-semibold tracking-tight">
                    {name}
                  </span>
                  <ArrowRight
                    size={16}
                    className="text-[var(--ember)] transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 10. Closing CTA band */}
      <CtaBand />
    </>
  );
}
